import { Link } from 'react-router-dom';
import { ArrowRight, Users, Trophy, MapPin, Wrench, Target, Bike, Mountain, Award } from 'lucide-react';
import { motion } from 'framer-motion';
import { CoreTeamSection } from '../components/Team/CoreTeamSection';
import { RoadmapSection } from '../components/Home/RoadmapSection';

export default function Home() {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-screen">
        <img
          src="https://storage.googleapis.com/nhvelo-f0124.appspot.com/images/1735895036171.jpg"
          alt="Mountain biking"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-white px-4">
          <motion.h1 
            className="text-5xl md:text-7xl font-bold mb-6 text-center"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Welcome to NHVelo
          </motion.h1>
          <motion.p 
            className="text-xl md:text-2xl mb-8 text-center max-w-3xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            United by a passion for the sport, NHVelo aims to shape the future of mountain biking in Romania.
          </motion.p>
          <motion.div 
            className="flex flex-col sm:flex-row gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            <Link
              to="/bikepark"
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-lg flex items-center gap-2 transition-colors"
            >
              Visit BikePark <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/team"
              className="bg-white hover:bg-gray-100 text-gray-900 px-8 py-3 rounded-lg flex items-center gap-2 transition-colors"
            >
              Meet Our Team <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold mb-6">Our Story</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Starting January 1, 2024, NHBike and Velociped Mountain united as NHVelo, combining their expertise to become a national leader in mountain biking. NHBike brings a track record of successful projects, while Velociped excels in training elite athletes.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <motion.div 
              className="bg-white p-8 rounded-xl shadow-lg transform hover:scale-105 transition-transform duration-300"
              variants={fadeIn}
            >
              <Users className="w-12 h-12 text-orange-500 mb-4" />
              <h3 className="text-xl font-bold mb-2">80+ Members</h3>
              <p className="text-gray-600">A growing community of passionate riders and elite athletes</p>
            </motion.div>
            <motion.div 
              className="bg-white p-8 rounded-xl shadow-lg transform hover:scale-105 transition-transform duration-300"
              variants={fadeIn}
            >
              <Trophy className="w-12 h-12 text-orange-500 mb-4" />
              <h3 className="text-xl font-bold mb-2">Elite Training</h3>
              <p className="text-gray-600">Professional coaching and development programs</p>
            </motion.div>
            <motion.div 
              className="bg-white p-8 rounded-xl shadow-lg transform hover:scale-105 transition-transform duration-300"
              variants={fadeIn}
            >
              <MapPin className="w-12 h-12 text-orange-500 mb-4" />
              <h3 className="text-xl font-bold mb-2">Premium Facilities</h3>
              <p className="text-gray-600">Custom transport vehicles and training facilities</p>
            </motion.div>
            <motion.div 
              className="bg-white p-8 rounded-xl shadow-lg transform hover:scale-105 transition-transform duration-300"
              variants={fadeIn}
            >
              <Wrench className="w-12 h-12 text-orange-500 mb-4" />
              <h3 className="text-xl font-bold mb-2">Trail Building</h3>
              <p className="text-gray-600">Dedicated to creating and maintaining world-class trails</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold mb-6">Our Vision</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-16">
              NHVelo has significant resources and a clear vision for the future. With our 10-year plan, we aim to:
            </p>
            <div className="grid md:grid-cols-3 gap-8 mt-12">
              <motion.div 
                className="bg-white p-8 rounded-xl shadow-lg transform hover:scale-105 transition-transform duration-300 relative overflow-hidden group"
                whileInView={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 20 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <Target className="w-16 h-16 text-orange-500 mb-6 mx-auto" />
                <h3 className="text-2xl font-bold mb-4">Support Athletes</h3>
                <p className="text-gray-600">Providing resources and training for competitive success</p>
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/nhvelo-f0124.appspot.com/o/NhVeloSite%2FScreenshot%202025-04-26%20at%2023.01.40-min.png?alt=media&token=f28c2f6d-9a92-4dc7-ad4b-9f5cf1373db9"
                  alt="Support Athletes"
                  className="mt-6 rounded-lg w-full h-48 object-cover"
                />
              </motion.div>
              <motion.div 
                className="bg-white p-8 rounded-xl shadow-lg transform hover:scale-105 transition-transform duration-300 relative overflow-hidden group"
                whileInView={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 20 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <Award className="w-16 h-16 text-blue-500 mb-6 mx-auto" />
                <h3 className="text-2xl font-bold mb-4">Develop Talent</h3>
                <p className="text-gray-600">Nurturing the next generation of mountain biking champions</p>
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/nhvelo-f0124.appspot.com/o/NhVeloSite%2FScreenshot%202025-04-25%20at%2000.07.01-min.png?alt=media&token=6b9832e5-4b2b-4881-a4fa-40e2a8b1895d"
                  alt="Develop Talent"
                  className="mt-6 rounded-lg w-full h-48 object-cover"
                />
              </motion.div>
              <motion.div 
                className="bg-white p-8 rounded-xl shadow-lg transform hover:scale-105 transition-transform duration-300 relative overflow-hidden group"
                whileInView={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 20 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <Mountain className="w-16 h-16 text-green-500 mb-6 mx-auto" />
                <h3 className="text-2xl font-bold mb-4">Build Trails</h3>
                <p className="text-gray-600">Creating and maintaining world-class mountain biking infrastructure</p>
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/nhvelo-f0124.appspot.com/o/NhVeloSite%2FScreenshot%202025-04-25%20at%2010.20.03-min.png?alt=media&token=c31ed4b6-749a-457b-b03e-fa0ce776da77"
                  alt="Build Trails"
                  className="mt-6 rounded-lg w-full h-48 object-cover"
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Roadmap Section */}
      <RoadmapSection />

      {/* Core Team Section */}
      <CoreTeamSection />
    </div>
  );
}